import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { State } from 'src/app/store';
import { Actions, ofType } from '@ngrx/effects';
import { Subject, combineLatest } from 'rxjs';
import { userRecordNameSelector, userRecordSelector } from 'src/app/user/store/user.selectors';
import { tap, filter, takeUntil } from 'rxjs/operators';
import { UpdateUserRecordAction, UpdateUserRecordErrorAction, UpdateUserRecordSuccessAction, FetchUserRecordAction } from 'src/app/user/store/user.actions';
import { ActivatedRoute } from '@angular/router';
import { MessageService } from 'src/app/global/services/message/message.abstract.service';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { Permission } from '@bciti/types';
import { pgRequetesFeatureSelector, organisationConfigurationSelector } from 'src/app/global/store/global.selectors';
import { authPermissionsSelector } from '../../../auth/store/auth.selectors';

@Component({
    selector: 'app-user-update',
    templateUrl: './user-update.component.html',
    styleUrls: ['./user-update.component.scss'],
})
export class UserUpdateComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject();

    private userid: string;

    public name$ = this.store.select(userRecordNameSelector);

    public loading = false;

    public t: any = false;

    public unregistered: boolean;

    public adminTabs = [];

    public userRecord$ = this.store.select(userRecordSelector).pipe(filter((record) => !!record?.id));

    constructor(
        private route: ActivatedRoute,
        private store: Store<State>,
        private actions$: Actions,
        private messageService: MessageService,
        private translateService: TranslateService,
    ) {}

    public ngOnInit(): void {
        combineLatest([this.store.select(organisationConfigurationSelector), this.store.select(pgRequetesFeatureSelector), this.store.select(authPermissionsSelector)])
            .pipe(
                takeUntil(this.destroy$),
                filter(([config]) => !!config?.adminTabs),
                tap(([config, pgRequetesIframe, permissions]) => {
                    let adminTabs = config.adminTabs.filter((tab) => {
                        if (permissions?.includes(tab.tag as Permission)) {
                            return true;
                        }
                        return false;
                    });

                    const hasRequests = adminTabs.some((tab) => tab.tag === 'REQUEST');
                    if (pgRequetesIframe?.active && hasRequests) {
                        adminTabs = adminTabs.filter((tab) => tab.tag !== 'REQUEST');
                    }
                    this.adminTabs = _.sortBy(adminTabs, 'order');
                }),
            )
            .subscribe();

        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            this.userid = params.get('id');
            if (this.userid) {
                this.store.dispatch(FetchUserRecordAction({ userid: this.userid }));
            }
        });

        this.translateService
            .get('pages.user-update')
            .pipe(takeUntil(this.destroy$))
            .subscribe((t) => (this.t = t));

        this.store
            .select(userRecordSelector)
            .pipe(
                takeUntil(this.destroy$),
                filter((userRecord) => !!userRecord),
                tap((userRecord) => {
                    this.unregistered = userRecord.unregistered || false;
                }),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateUserRecordAction),
                tap(() => (this.loading = true)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateUserRecordSuccessAction),
                tap(() => (this.loading = false)),
            )
            .subscribe();

        this.actions$
            .pipe(
                takeUntil(this.destroy$),
                ofType(UpdateUserRecordErrorAction),
                tap(() => (this.loading = false)),
                tap(({ error }) => {
                    if (error?.status === 409) {
                        this.messageService.error(this.t?.errors?.emailAlreadyUsed);
                    } else {
                        this.messageService.error(error?.error?.messages?.[0] || this.t.instant('frontEnd.common.errors.unknown'));
                    }
                }),
            )
            .subscribe();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
